import { AxiosResponse } from 'axios';
import { addDays, format } from 'date-fns';
import qs from 'qs';
import { trackPromise } from 'react-promise-tracker';
import { CompetitorPriceDTO, CompetitorsInfo, IGerenciadorPrecosDatapoint as RowData, UnknownObject } from '../../../@types';
import { IPAMaths, api } from '../../../lib';
import { withPendingEditedPrice } from '../../../store/priceManagerStore';
import { RuleModelReturn } from '../Estrategia/pages/RegrasDePreco/lib';
import { PriceManagerDatapointsModel } from './hooks/usePriceManagerDatapointsModel';
import { GET_ALL_PRODUCTS, GET_COMPETITORS_INFO, GET_COMPETITORS_INFO_OFFER_PRICE } from './utils';
import { getValidData } from './utils/getValidData';

export type LoadFilterDataOption = {
  isModal?: boolean;
  size?: number;
  productId?: string[];
  productFamilyId?: string[];
  gtin?: string[];
  description?: string[];
  abcClass?: string[];
  categoryLevel1?: string[];
  categoryLevel2?: string[];
  categoryLevel3?: string[];
  categoryLevel4?: string[];
  categoryLevel5?: string[];
  categoryLevel6?: string[];
  categoryLevel7?: string[];
};

export const loadFilterDataOptionDefault: LoadFilterDataOption = {
  isModal: false,
  size: 20,
};

export const salvarSimulacao = (data: any) =>
  trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/simulation/forecast/save`,
      method: 'POST',
      expectToken: true,
      data: JSON.stringify(data),
    }).then(resp => resp),
  );

export const novoCalculoPrevisao = (currentPrice: any, newPrice: any, storeId: any, productId: any, pmz: any) =>
  trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/simulation/forecast`,
      method: 'GET',
      expectToken: true,
      params: {
        currentPrice,
        newPrice,
        storeId,
        productId,
        pmz,
      },
    }).then(resp => resp),
  );

export type ApplyPricesParams = {
  selectedAllProducts: boolean;
  applyToFamily: boolean;
  productsSelect: {
    productId: RowData['productId'];
    storeId: RowData['storeId'];
    price: RowData['price'];
    productsToBePricedId: RowData['productsToBePricedId'];
  }[];
  productsUnSelect: {
    productId: RowData['productId'];
    storeId: RowData['storeId'];
    productsToBePricedId: RowData['productsToBePricedId'];
  }[];
  filters?: UnknownObject;
  datapointExhibitionType: 'PRODUCT' | 'FAMILY' | 'PRODUCT_CLUSTER' | 'PRODUCT_CLUSTER_FAMILY';
};

export const applyPrices = async (params: ApplyPricesParams) => {
  const res = await withPendingEditedPrice(() =>
    trackPromise(
      api.patch('/all-products/apply', params, {
        expectToken: true,
      }),
      'get-datapoints-revisao',
    ),
  );

  return res;
};

export type GetPriceCompositionParams = {
  productId: RowData['productId'];
  storeId: RowData['storeId'];
  clusterId?: RowData['clusterId'];
  datapointExhibitionType?: 'PRODUCT' | 'FAMILY' | 'PRODUCT_CLUSTER' | 'PRODUCT_CLUSTER_FAMILY';
};

export const getPriceComposition = ({ productId, storeId, clusterId, datapointExhibitionType }: GetPriceCompositionParams) =>
  trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/all-products/price/composition`,
      method: 'GET',
      expectToken: true,
      params: getValidData({
        productId,
        storeId,
        ...(clusterId && { clusterId }),
        ...(datapointExhibitionType && { datapointExhibitionType }),
      }),
      paramsSerializer(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    }).then(response => response.data),
    'price-composition',
  );

export type IGetCompetitorsInfo = {
  productId: RowData['productId'];
  storeId: RowData['storeId'];
  productsToBePricedId: RowData['productsToBePricedId'];
  competitorDays: RowData['competitorDays'];
  productFamilyId?: RowData['productFamilyId'];
  competitorStoreId?: CompetitorPriceDTO['competitorStoreId'][];
  unselectedCompetitors: RuleModelReturn['competitors'];
  clusterId: string | null;
  clusterRule?: boolean;
  factorProductMaster?: RowData['factorProductMaster'];
};

export namespace GetCompetitorsInfo {
  export type Params = IGetCompetitorsInfo;
  export type Result = CompetitorsInfo;
}

export const getCompetitorsInfo = async (params: GetCompetitorsInfo.Params): Promise<GetCompetitorsInfo.Result> => {
  const { url, config } = GET_COMPETITORS_INFO(params);

  const { data } = await trackPromise(api.get<CompetitorsInfo>(url, config), `competitor-row-${params.storeId}-${params.productId}`);

  return data;
};

export const getCompetitorsInfoOfferPrice = async (params: GetCompetitorsInfo.Params): Promise<GetCompetitorsInfo.Result> => {
  const { url, config } = GET_COMPETITORS_INFO_OFFER_PRICE(params);

  const { data } = await trackPromise(api.get<CompetitorsInfo>(url, config), `competitor-row-${params.storeId}-${params.productId}`);

  return data;
};

const removeEmptyFilterValues = (data: Record<string, unknown>) =>
  Object.entries(data).reduce<Record<string, unknown>>((params, [key, value]) => {
    if (Array.isArray(value) && !value.length) return params;
    if (value === undefined || value === null || value === '') return params;
    return {
      ...params,
      [key]: value,
    };
  }, {});

export const getFamiliaProdutos = (productFamilyId = '', { size = 20, isModal = false, ...filters }: LoadFilterDataOption = loadFilterDataOptionDefault) =>
  trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/products/families/id`,
      method: 'POST',
      expectToken: true,
      params: {
        size: isModal ? 1000 : size,
      },
      data: removeEmptyFilterValues({
        ...filters,
        ...(productFamilyId && {
          query: productFamilyId,
        }),
      }),
    }).then(resp => resp?.data),
  );

export const getLojasRevisao = (search: any) =>
  trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/products/store`,
      method: 'GET',
      expectToken: true,
      params: {
        size: 20,
        ...(search && {
          search,
        }),
      },
    }).then(resp => resp?.data),
  );

export namespace GetDatapointsRevisao {
  export type Params = UnknownObject;
  export type Response = {
    content: RowData[];
    number: number;
    size: number;
    totalElements: number;
    hasContent: boolean;
    first: boolean;
    last: boolean;
    numberOfElements: number;
    totalPages: number;
    pageable: {
      sort: any[];
      offset: number;
      pageSize: number;
      pageNumber: number;
      paged: boolean;
      unpaged: boolean;
    };
    empty: boolean;
  };
}

export const onSuccessGetDatapointsRevisaoItem = (rowData: RowData): RowData => {
  const offerDetails = rowData.offerDetails
    ? {
        ...rowData.offerDetails,
        newCompetitivenessPrice: IPAMaths.novaCompetitividade(rowData.offerDetails.price ?? null, rowData.offerDetails.competitorsPrice ?? null),
      }
    : rowData.offerDetails;

  const { price, competitorsPrice, retailPrice, salesTaxPercentage, expensesPercent, cost, wholesale } = rowData;

  const novaCompetitividade = IPAMaths.novaCompetitividade(price, competitorsPrice);

  const retailCpi = IPAMaths.novaCompetitividade(retailPrice, competitorsPrice);

  const novaMargem = IPAMaths.novaMargem(price, salesTaxPercentage, expensesPercent, cost);

  const wholesaleCpi = IPAMaths.precoWithCpi(wholesale?.price ?? null, competitorsPrice);

  return {
    ...rowData,
    offerDetails,
    expirationDate: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    revenueForecastSevenDays: 13000,
    novaMargem,
    novaCompetitividade,
    retailCpi,
    // Enable Calculate button by default for all rows, regardless of existing demand forecast data
    // This allows users to recalculate or calculate for the first time without needing to edit prices first
    novoCalculo: true,
    ...(wholesale && {
      wholesale: {
        ...wholesale,
        newCompetitivenessPrice: wholesaleCpi,
        discountPercentage: wholesale.discountPercentage,
      },
    }),
  };
};

export const onSuccessGetDatapointsRevisao = (data: GetDatapointsRevisao.Response): GetDatapointsRevisao.Response => {
  return {
    ...data,
    content: data.content.map(onSuccessGetDatapointsRevisaoItem),
  };
};

export const getDatapointsRevisao = async (params: GetDatapointsRevisao.Params): Promise<GetDatapointsRevisao.Response> => {
  try {
    const { url, config } = GET_ALL_PRODUCTS(params);

    const { data } = await trackPromise(
      api.post<GetDatapointsRevisao.Response>(url, config.data, {
        expectToken: true,
        params: {
          sort: (config.params as { sort?: unknown; size?: unknown; page?: unknown }).sort,
          size: (config.params as { sort?: unknown; size?: unknown; page?: unknown }).size,
          page: (config.params as { sort?: unknown; size?: unknown; page?: unknown }).page,
        },
      }),
      'get-datapoints-revisao',
    );

    return onSuccessGetDatapointsRevisao(data);
  } catch (error) {
    return onSuccessGetDatapointsRevisao({
      content: [],
      number: 0,
      size: 25,
      totalElements: 0,
      hasContent: false,
      first: true,
      last: true,
      numberOfElements: 0,
      totalPages: 0,
      pageable: {
        sort: [],
        offset: 0,
        pageSize: 25,
        pageNumber: 0,
        paged: true,
        unpaged: false,
      },
      empty: true,
    });
  }
};

export type SaveEditedDatapoint = {
  data: Pick<RowData, 'productId' | 'storeId' | 'price' | 'productsToBePricedId'> & {
    datapointExhibitionType: string;
    clusterId: string | null;
  };
  response: AxiosResponse<RowData[] & { datapointExhibitionType: string }>;
};

export function saveEditedDatapoint({
  price,
  datapointExhibitionType,
  clusterId,
  ...data
}: SaveEditedDatapoint['data']): Promise<SaveEditedDatapoint['response']> {
  return withPendingEditedPrice(() =>
    trackPromise(
      api({
        url: `${process.env.REACT_APP_IPA_URL}/all-products/update/price`,
        method: 'POST',
        expectToken: true,
        params: getValidData({
          editedPrice: price,
          datapointExhibitionType,
          ...data,
          ...(datapointExhibitionType === 'PRODUCT_CLUSTER' || datapointExhibitionType === 'PRODUCT_CLUSTER_FAMILY' ? { clusterId } : {}),
        }),
        paramsSerializer(params) {
          return qs.stringify(params, { arrayFormat: 'repeat' });
        },
      })
        .then(resp => {
          return resp;
        })
        .catch(error => {
          throw error;
        }),
      `edit-datapoint-${data.productId}-${data.storeId}`,
    ),
  );
}

export namespace SaveEditedWholesaleDatapoint {
  export type Data = {
    productId: RowData['productId'];
    storeId: RowData['storeId'];
    price: RowData['price'];
    triggerCount: number | null;
  };
  export type Result = AxiosResponse<unknown>;
}

export const saveEditedWholesaleDatapoint = async (data: SaveEditedWholesaleDatapoint.Data): Promise<SaveEditedWholesaleDatapoint.Result> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { price, ...rest } = data;
  const updatedData = { ...rest, editedPrice: data.price };
  return withPendingEditedPrice(() =>
    api.post('/all-products/update/price/wholesale', updatedData, {
      expectToken: true,
    }),
  );
};

export namespace SaveEditedOfferDatapoint {
  export type Data = {
    productId: RowData['productId'];
    storeId: RowData['storeId'];
    price: RowData['price'];
    productFamilyId?: RowData['productFamilyId'];
    clusterId?: RowData['clusterId'];
    datapointExhibitionType: SaveEditedDatapoint['data']['datapointExhibitionType'];
  };
  export type Result = AxiosResponse<unknown>;
}

export const saveEditedOfferDatapoint = async (data: SaveEditedOfferDatapoint.Data): Promise<SaveEditedOfferDatapoint.Result> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { price, clusterId, datapointExhibitionType, productFamilyId, ...rest } = data;
  const updatedData = getValidData({
    ...rest,
    editedPrice: data.price,
    datapointExhibitionType,
    productFamilyId: productFamilyId ?? null,
    clusterId: clusterId ?? null,
  });
  return withPendingEditedPrice(() =>
    api.post('/all-products/update/price/offer', updatedData, {
      expectToken: true,
    }),
  );
};

export namespace UndoEditedDatapoint {
  export type Params = {
    productId: RowData['productId'];
    storeId: RowData['storeId'];
    productsToBePricedId: RowData['productsToBePricedId'];
  };
  export type Response = AxiosResponse<RowData>;
}

export function undoEditedDatapoint(params: UndoEditedDatapoint.Params) {
  return trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/all-products/undo-price-change`,
      method: 'DELETE',
      expectToken: true,
      params,
      paramsSerializer(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    }).then(resp => resp),
    `edit-datapoint-${params.productId}-${params.storeId}`,
  );
}

type GetTotalPricesReturn = {
  totalPrices: number;
  totalPricesFiltered: number;
  totalSugestedPrices: number;
  totalSugestedFiltered: number;
  totalAppliedPrices: number;
  totalAppliedFiltered: number;
  totalNewCosts: number;
  totalNewCostsFiltered: number;
};

export const initialTotalPricesData: GetTotalPricesReturn = {
  totalPrices: 0,
  totalPricesFiltered: 0,
  totalSugestedPrices: 0,
  totalSugestedFiltered: 0,
  totalAppliedPrices: 0,
  totalAppliedFiltered: 0,
  totalNewCosts: 0,
  totalNewCostsFiltered: 0,
};

export async function getTotalPrices(data?: PriceManagerDatapointsModel) {
  try {
    const response = await api.post<GetTotalPricesReturn>('/all-products/total-prices', data, {
      expectToken: true,
      paramsSerializer(params) {
        return qs.stringify(params, { arrayFormat: 'comma' });
      },
    });
    return response.data;
  } catch {
    return initialTotalPricesData;
  }
}

type GetVariationPricesReturn = {
  marginCurrent: number;
  marginNew: number;
  cpiCurrent: number;
  cpiNew: number;
  profitCurrent: number;
  profitNew: number;
};

export const initialVariationPrices: GetVariationPricesReturn = {
  marginCurrent: 0,
  marginNew: 0,
  cpiCurrent: 0,
  cpiNew: 0,
  profitCurrent: 0,
  profitNew: 0,
};

export async function getVariationPrices(data?: PriceManagerDatapointsModel) {
  try {
    const response = await trackPromise(
      api.post<GetVariationPricesReturn>('/all-products/variation-price', data, {
        expectToken: true,
        paramsSerializer(params) {
          return qs.stringify(params, { arrayFormat: 'comma' });
        },
      }),
      'variation-price',
    );
    return response.data;
  } catch {
    return initialVariationPrices;
  }
}
